import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import { useEventActions } from 'src/store/event/actions';
import { successMsg, errorMsg, infoMsg } from 'src/components/utilities/utils';
import Editor from 'src/components/Editor/EventEditor';
import {
    CustomFieldsModel,
    DeleteRegistrationFieldModal,
    RegistrationFieldModel
} from 'src/models/eventModel';
import ToastMessage from 'src/components/ToastContainer';
import DeleteRegistrationModal from 'src/components/admin/events/components/DeleteRegistrationModal';

const Toggle: React.FC<{ checked: boolean; onChange: () => void; disabled?: boolean }> = ({
    checked,
    onChange,
    disabled
}) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onChange}
        className={`relative w-9 h-5 rounded-full flex-shrink-0 transition-colors ${checked ? 'bg-indigo-600' : 'bg-gray-300'
            } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
        <span
            className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
            style={{ left: checked ? '18px' : '2px' }}
        />
    </button>
);

const GripIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-gray-300">
        <circle cx="8" cy="6" r="1.5" />
        <circle cx="8" cy="12" r="1.5" />
        <circle cx="8" cy="18" r="1.5" />
        <circle cx="16" cy="6" r="1.5" />
        <circle cx="16" cy="12" r="1.5" />
        <circle cx="16" cy="18" r="1.5" />
    </svg>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
    <div className="bg-indigo-50 text-sm font-semibold text-gray-800 px-4 py-2 rounded-md">
        {children}
    </div>
);

const RegistrationFields = ({ setCurrentStep }: any) => {
    const dispatch = useDispatch();
    const { eventId } = useParams<string>();
    const eventActions = useEventActions(dispatch);

    const [eventType, setEventType] = useState<string | number>('1');
    const [deleteData, setDeleteData] = useState<DeleteRegistrationFieldModal>();
    const closeAddModalRef = useRef<HTMLButtonElement>(null);
    const [customPrivacyEditorState, setCustomPrivacyEditorState] = useState<string>('');
    const { singleEvent, registration, isRegistrationFieldSaved, isRegistrationFieldDeleted, isRegistrationFieldUpdated, message, isEventUpdated } =
        useSelector((state: RootState) => {
            return {
                singleEvent: state.event.singleEvent,
                registration: state.event.registration,
                isRegistrationFieldSaved: state.event.isRegistrationFieldSaved,
                isRegistrationFieldDeleted: state.event.isRegistrationFieldDeleted,
                isRegistrationFieldUpdated: state.event.isRegistrationFieldUpdated,
                message: state.event.message,
                isEventUpdated: state.event.isEventUpdated
            };
        });

    const {
        handleSubmit: handleOptionsSubmit,
        reset: resetOptions,
        setValue: setOptionValue,
        watch: watchOption
    } = useForm<any>({
        defaultValues: {
            id: 0,
            displayRegistrationForm: false,
            memberRegistrationRequired: false,
            isPasswordRequired: false,
            isPrivacyStatment: false,
            privacyStatmentValue: ''
        }
    });

    const displayRegistrationForm = watchOption('displayRegistrationForm');
    const memberRegistrationRequired = watchOption('memberRegistrationRequired');
    const isPasswordRequired = watchOption('isPasswordRequired');
    const isPrivacyStatment = watchOption('isPrivacyStatment');
    const privacyStatmentValue = watchOption('privacyStatmentValue');

    useEffect(() => {
        if (singleEvent && singleEvent.id) {
            resetOptions({
                id: singleEvent.id,
                displayRegistrationForm: singleEvent.displayRegistrationForm ?? false,
                memberRegistrationRequired: singleEvent.memberRegistrationRequired ?? false,
                isPasswordRequired: singleEvent.isPasswordRequired ?? false,
                isPrivacyStatment: singleEvent.isPrivacyStatment ?? false,
            });
            setCustomPrivacyEditorState(singleEvent.privacyStatmentValue);
        }
    }, [singleEvent]);

    const onOptionsSubmit = (data: any) => {
        const payloadData = {
            id: data.id,
            patchJsonData: [
                { op: 'replace', path: 'displayRegistrationForm', value: data.displayRegistrationForm },
                { op: 'replace', path: 'memberRegistrationRequired', value: data.memberRegistrationRequired },
                { op: 'replace', path: 'isPasswordRequired', value: data.isPasswordRequired },
                { op: 'replace', path: 'isPrivacyStatment', value: data.isPrivacyStatment },
                { op: 'replace', path: 'privacyStatmentValue', value: customPrivacyEditorState }
            ]
        };
        eventActions.updateEventRequest(payloadData);
        setCurrentStep(5)
    };

    const { reset } = useForm<CustomFieldsModel>();
    const {
        register,
        handleSubmit,
        reset: resetFieldForm,
        formState: { errors }
    } = useForm<RegistrationFieldModel>();

    useEffect(() => {
        eventActions.getEventRegistrationsRequest(eventId);
    }, []);

    useEffect(() => {
        if (isRegistrationFieldSaved || isRegistrationFieldDeleted || isRegistrationFieldUpdated)
            eventActions.getEventRegistrationsRequest(eventId);
        if (isRegistrationFieldUpdated === true) {
            successMsg('Success');
        } else if (isRegistrationFieldDeleted === true) errorMsg('Success');
        else if (isRegistrationFieldSaved === true) successMsg('Success');

        eventActions.resetEventStateRequest();
    }, [eventId, isRegistrationFieldSaved, isRegistrationFieldDeleted, isRegistrationFieldUpdated]);

    useEffect(() => {
        if (isRegistrationFieldSaved === true) {
            reset({
                id: 0,
                eventid: '',
                description: '',
                required: true,
                type: 0,
                isenable: true,
                isdefault: true,
                alwaysrequired: true,
                passcoderequiredonlogin: true,
                passcodes: ''
            });
            resetFieldForm({
                description: '',
                type: 1,
                alwaysrequired: true,
                passcoderequiredonlogin: false,
                passcodes: ''
            } as any);
            setEventType('1');
            closeAddModalRef.current?.click();
        }
    }, [isRegistrationFieldSaved]);
    useEffect(() => {
        if (eventId && isEventUpdated) {
            eventActions.getSingleEventRequest(eventId);
        }
    }, [eventId, isEventUpdated]);
    const onSubmit = (data: any) => {
        const isDuplicate = (registration?.customFields || []).some(
            (customFieldData: any) => customFieldData.description === data.description
        );
        if (isDuplicate) {
            errorMsg('Given name is already exists');
            return;
        }
        const insertData = {
            ...data,
            eventId
        };
        eventActions.saveRegistrationFieldRequest(insertData);
    };

    const handleChangeisRequired = (
        isRequiredValue: boolean,
        registrationFieldID: number | string
    ) => {
        const patchisRequiredData = {
            registration_field_id: registrationFieldID,
            isrequired: isRequiredValue
        };
        eventActions.updateIsRequiredRequest(patchisRequiredData);
    };

    const handleChangeisEnable = (isEnableValue: boolean, registrationFieldId: number | string) => {
        const patchIsEnableData = {
            registration_field_id: registrationFieldId,
            isEnable: isEnableValue
        };
        eventActions.updateIsEnableRequest(patchIsEnableData);
    };

    const handleChangeisPasscodeRequired = (
        isPasscodeRequiredValue: boolean,
        registrationFieldId: number | string
    ) => {
        const patchIsPasscodeRequiredData = {
            registration_field_id: registrationFieldId,
            isPasscodeRequired: isPasscodeRequiredValue
        };
        eventActions.updateIsPasscodeRequiredRequest(patchIsPasscodeRequiredData);
    };

    const formatEventType = (type: number) => {
        if (type === 1) return 'Textbox';
        else if (type === 2) return 'Checkbox';
        else if (type === 3) return 'Passcode';
        else return 'None';
    };

    const renderFieldRows = (hidden: boolean) => {
        const rows = (registration?.customFields || []).filter((customField: any) =>
            hidden ? customField.ishiddenfield : customField.ishiddenfield === null || !customField.ishiddenfield
        );

        if (!registration?.customFields || rows.length === 0) {
            return (
                <tr>
                    <td className="text-center py-3 text-sm text-gray-400" colSpan={8}>
                        No Data Found
                    </td>
                </tr>
            );
        }

        return rows.map((customField: any, index: number) => (
            <tr key={index} className="border-b border-gray-100 last:border-b-0">
                <td className="p-3">
                    <GripIcon />
                </td>
                <td className="p-3 text-sm text-gray-800">{customField.description}</td>
                <td className="p-3 text-sm text-gray-500">{formatEventType(customField.type)}</td>
                <td className="p-3">
                    <Toggle
                        checked={customField.required === true}
                        onChange={() => handleChangeisRequired(!customField.required, customField.id)}
                    />
                </td>
                <td className="p-3">
                    <Toggle
                        checked={!!customField.isenable}
                        onChange={() => handleChangeisEnable(!customField.isenable, customField.id)}
                    />
                </td>
                <td className="p-3">
                    {customField.type === 5 && (
                        <Toggle
                            checked={!!customField.passcoderequiredonlogin}
                            onChange={() =>
                                handleChangeisPasscodeRequired(
                                    !customField.passcoderequiredonlogin,
                                    customField.id
                                )
                            }
                        />
                    )}
                </td>
                <td className="p-3 text-sm text-gray-500">{customField.passcodes}</td>
                <td className="p-3 text-gray-400">
                    {!customField.isdefault && (
                        <span
                            data-bs-toggle="modal"
                            data-bs-target="#DeleteRegistrationModal"
                            className="action-menu inline-flex items-center cursor-pointer hover:text-red-500"
                            onClick={() =>
                                setDeleteData({
                                    regsitrationFieldId: customField.id,
                                    eventName: customField.description
                                })
                            }>
                            <i className="fas fa-trash w-6"></i>
                        </span>
                    )}
                </td>
            </tr>
        ));
    };

    const tableHead = (
        <thead>
            <tr className="text-xs font-semibold text-gray-400 uppercase bg-gray-50 whitespace-nowrap">
                <th className="p-3 text-left w-8"></th>
                <th className="p-3 text-left">Field Name</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Required</th>
                <th className="p-3 text-left">Enable</th>
                <th className="p-3 text-left">Display on Login</th>
                <th className="p-3 text-left">Passcode</th>
                <th className="p-3 text-left">Actions</th>
            </tr>
        </thead>
    );

    return (
        <>
            <ToastMessage />
            <main className="flex-1 flex flex-col min-w-0">
                <div className="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col">
                    <div className="px-4 sm:px-8 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-900 m-0">Registration Setup</h2>
                        <p className="text-sm text-gray-400 mt-1">
                            Configure how attendees register for this event and which information is collected.
                        </p>
                    </div>

                    <form onSubmit={handleOptionsSubmit(onOptionsSubmit)}>
                        <div className="px-4 sm:px-8 py-5 sm:py-6 space-y-6">
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                <div className='min-w-0'>
                                    <SectionHeader>Registration Options</SectionHeader>
                                    <div className="mt-4 space-y-4">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <Toggle
                                                checked={!!displayRegistrationForm}
                                                onChange={() =>
                                                    setOptionValue('displayRegistrationForm', !displayRegistrationForm, {
                                                        shouldDirty: true
                                                    })
                                                }
                                            />
                                            <div>
                                                <p className="m-0 text-sm font-semibold text-gray-700">
                                                    Display registration form
                                                </p>
                                                <p className="m-0 text-xs text-gray-400 mt-0.5">
                                                    Show the registration form on the event page.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <Toggle
                                                checked={!!memberRegistrationRequired}
                                                onChange={() =>
                                                    setOptionValue(
                                                        'memberRegistrationRequired',
                                                        !memberRegistrationRequired,
                                                        { shouldDirty: true }
                                                    )
                                                }
                                            />
                                            <div>
                                                <p className="m-0 text-sm font-semibold text-gray-700">
                                                    Require registration before accessing the event
                                                </p>
                                                <p className="m-0 text-xs text-gray-400 mt-0.5">
                                                    All users must register to access this event.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <Toggle
                                                checked={!!isPasswordRequired}
                                                onChange={() =>
                                                    setOptionValue('isPasswordRequired', !isPasswordRequired, {
                                                        shouldDirty: true
                                                    })
                                                }
                                            />
                                            <div>
                                                <p className="m-0 text-sm font-semibold text-gray-700">
                                                    Require a password on registration
                                                </p>
                                                <p className="m-0 text-xs text-gray-400 mt-0.5">
                                                    Users will need to create a password when registering.
                                                </p>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <div className="min-w-0">
                                    <SectionHeader>Privacy Statement</SectionHeader>
                                    <div className="mt-4">
                                        <div className="flex items-start gap-3">
                                            <Toggle
                                                checked={!!isPrivacyStatment}
                                                onChange={() =>
                                                    setOptionValue('isPrivacyStatment', !isPrivacyStatment, {
                                                        shouldDirty: true
                                                    })
                                                }
                                            />
                                            <div>
                                                <p className="m-0 text-sm font-semibold text-gray-700">
                                                    Enable privacy statement
                                                </p>
                                                <p className="m-0 text-xs text-gray-400 mt-0.5">
                                                    Show a privacy statement on the registration form.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                Custom privacy statement
                                            </label>
                                            <div className="privacy-editor-wrap w-full min-w-0 overflow-hidden border border-gray-200 rounded-lg">
                                                <Editor
                                                    editorState={customPrivacyEditorState}
                                                    setEditorState={setCustomPrivacyEditorState}
                                                />
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Registration Fields */}
                            <div>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <SectionHeader>Registration Fields</SectionHeader>
                                    </div>
                                    <button
                                        type="button"
                                        data-bs-toggle="modal"
                                        data-bs-target="#AddRegistrationFieldModal"
                                        className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5">
                                            <line x1="12" y1="5" x2="12" y2="19" />
                                            <line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                        Add Field
                                    </button>
                                </div>
                                <p className="text-sm text-gray-400 mt-1">
                                    Choose which fields to display on the registration form and whether they are
                                    required.
                                </p>

                                <div className="mt-3 overflow-x-auto border border-gray-100 rounded-lg">
                                    <table className="w-full table-auto min-w-[760px]">
                                        {tableHead}
                                        <tbody>{renderFieldRows(false)}</tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Hidden Fields */}
                            <div>
                                <SectionHeader>
                                    Hidden Fields <span className="text-gray-400 font-normal">(Optional)</span>
                                </SectionHeader>
                                <p className="text-sm text-gray-400 mt-1">
                                    These fields are not shown on the registration form but can be captured and stored
                                    with registration data.
                                </p>

                                <div className="mt-3 overflow-x-auto border border-gray-100 rounded-lg">
                                    <table className="w-full table-auto min-w-[760px]">
                                        {tableHead}
                                        <tbody>{renderFieldRows(true)}</tbody>
                                    </table>
                                </div>

                                <div className="flex items-start gap-2 bg-indigo-50 text-sm text-gray-600 rounded-lg px-4 py-3 mt-3">
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        className="mt-0.5 flex-shrink-0 text-indigo-600">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="16" x2="12" y2="12" />
                                        <line x1="12" y1="8" x2="12.01" y2="8" />
                                    </svg>
                                    Enable any of these fields if you need to track this information or include it in
                                    your registration export.
                                </div>
                            </div>
                        </div>

                        <div className="flex-1" />

                        {/* Footer */}
                        <div className="flex justify-between gap-3 px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100">
                            <button
                                onClick={() => setCurrentStep(3)}
                                type="button"
                                className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg px-4 sm:px-5 py-2.5 hover:bg-gray-50">
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5">
                                    <line x1="19" y1="12" x2="5" y2="12" />
                                    <polyline points="12 19 5 12 12 5" />
                                </svg>
                                Back
                            </button>
                            <button
                                type="submit"
                                className="flex-1 sm:flex-none justify-center flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-4 sm:px-5 py-2.5">
                                Next: Images &amp; Style
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </button>
                        </div>
                    </form>
                </div>
            </main>

            {/* Add Field Modal  */}
            <div
                className="modal fade"
                id="AddRegistrationFieldModal"
                tabIndex={-1}
                aria-labelledby="AddRegistrationFieldModalLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content rounded-xl">
                        <div className="modal-header px-6 pt-5 pb-3 border-b border-gray-100">
                            <h5 className="text-base font-semibold text-gray-900 m-0" id="AddRegistrationFieldModalLabel">
                                Add Registration Field
                            </h5>

                            <button
                                type="button"
                                ref={closeAddModalRef}

                                className="dismiss-btn"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="modal-body px-6 py-5">
                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Name</label>
                                    <input
                                        type="text"
                                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                        {...register('description', { required: true })}
                                    />
                                    {errors.description?.type === 'required' && (
                                        <p className="text-red-700 text-xs mt-1">Required Field</p>
                                    )}
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type</label>
                                    <select
                                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                        {...register('type', { required: true })}
                                        onChange={(e) => setEventType(e.target.value)}>
                                        <option value={1}>Textbox</option>
                                        <option value={2}>Checkbox</option>
                                        <option value={3}>Passcode</option>
                                    </select>
                                    {errors.type?.type === 'required' && (
                                        <p className="text-red-700 text-xs mt-1">Required Field</p>
                                    )}
                                </div>

                                <div className="flex items-center gap-3 mb-4 form-switch pl-0" >
                                    <input
                                        type="checkbox"
                                        className="form-check-input switch-btn"
                                        id="alwaysrequired"
                                        defaultChecked
                                        {...register('alwaysrequired', { required: false })}
                                    />
                                    <label htmlFor="alwaysrequired" className="text-sm font-semibold text-gray-700 m-0">
                                        Required
                                    </label>
                                </div>

                                {eventType === '3' && (
                                    <>
                                        <div className="flex items-center gap-3 mb-4 form-switch pl-0">
                                            <input
                                                type="checkbox"
                                                className="form-check-input switch-btn"
                                                id="passcoderequiredonlogin"
                                                {...register('passcoderequiredonlogin')}
                                            />
                                            <label
                                                htmlFor="passcoderequiredonlogin"
                                                className="text-sm font-semibold text-gray-700 m-0">
                                                Display passcode on Login
                                            </label>
                                        </div>
                                        <div className="mb-2">
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                                Enter Passcode
                                            </label>
                                            <input
                                                type="text"
                                                id="passcodes"
                                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                                {...register('passcodes')}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="modal-footer flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    data-bs-dismiss="modal"
                                    className="text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg px-5 py-2.5 hover:bg-gray-50">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-5 py-2.5">
                                    Add
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <DeleteRegistrationModal deleteData={deleteData} />
        </>
    );
};

export default RegistrationFields;